// Client-side dominant-color extraction — no external deps.
// Given a File (PNG/JPEG/WEBP), returns { dominant, palette } where
// - `dominant` is the single best brand color (saturated, non-background)
// - `palette` is up to 5 candidate swatches the user can click to pick
//
// Strategy: draw the image at 48x48 into a canvas, walk pixels, ignore
// near-white/near-black/low-alpha (background + shadows), bin colors into
// 24 hue buckets weighted by saturation, and rank by (bucket weight ×
// saturation). Good enough for logos, ~2ms per image, works offline.

const rgbToHex = (r, g, b) =>
  "#" + [r, g, b].map(v => Math.max(0, Math.min(255, v | 0)).toString(16).padStart(2, "0")).join("");

const rgbToHsl = (r, g, b) => {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4;
    }
    h *= 60;
  }
  return [h, s, l];
};

export async function extractLogoPalette(file) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = url;
    });
    const size = 48;
    const cvs = document.createElement("canvas");
    cvs.width = size; cvs.height = size;
    const ctx = cvs.getContext("2d");
    ctx.drawImage(img, 0, 0, size, size);
    const { data } = ctx.getImageData(0, 0, size, size);

    // Bin by hue (24 buckets, 15° each). Track total saturation + a
    // representative pixel per bucket so we can return real hex codes.
    const BUCKETS = 24;
    const bins = Array.from({ length: BUCKETS }, () => ({
      weight: 0, r: 0, g: 0, b: 0, count: 0,
    }));

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3];
      if (a < 200) continue;                    // transparent
      const [h, s, l] = rgbToHsl(r, g, b);
      if (l > 0.94 || l < 0.06) continue;       // near-white / near-black
      if (s < 0.15) continue;                   // near-gray (background)
      const idx = Math.min(BUCKETS - 1, Math.floor((h % 360) / (360 / BUCKETS)));
      const bin = bins[idx];
      // Weight saturated + mid-lightness pixels higher — that's where
      // logo brand colors live.
      const midLight = 1 - Math.abs(l - 0.5) * 2;
      const w = s * s * (0.5 + 0.5 * midLight);
      bin.weight += w;
      bin.r += r * w; bin.g += g * w; bin.b += b * w;
      bin.count += w;
    }

    const swatches = bins
      .filter(b => b.count > 0)
      .map(b => ({
        hex: rgbToHex(b.r / b.count, b.g / b.count, b.b / b.count),
        weight: b.weight,
      }))
      .sort((a, b) => b.weight - a.weight);

    if (!swatches.length) {
      // Pure black/white logo → fall back to slate-900 so header still
      // gets non-white contrast.
      return { dominant: "#0f172a", palette: ["#0f172a"] };
    }

    // Dedup near-identical hues (within 12 hue degrees).
    const uniq = [];
    for (const s of swatches) {
      const [h] = rgbToHsl(
        parseInt(s.hex.slice(1, 3), 16),
        parseInt(s.hex.slice(3, 5), 16),
        parseInt(s.hex.slice(5, 7), 16),
      );
      if (uniq.every(u => Math.abs(u.h - h) > 12 && Math.abs((u.h - h + 360) % 360) > 12)) {
        uniq.push({ ...s, h });
        if (uniq.length >= 5) break;
      }
    }

    return {
      dominant: uniq[0].hex,
      palette: uniq.map(u => u.hex),
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}
